import type { InputDeviceResponse } from '$lib/types/input_device';
import type { DeviceType } from '$lib/types/input_device';

export const DEVICE_TYPE_LABELS: Record<DeviceType, string> = {
  standard_keypad: 'Keypad',
  logic_board: 'Logic Board',
  keycard: 'Keycard',
  corridor_keypad: 'Corridor Keypad'
};

export const DEVICE_TYPE_TO_GROUP_ID: Record<DeviceType, number> = {
  corridor_keypad: 2,
  standard_keypad: 3,
  keycard: 4,
  logic_board: 3
};

export const DEVICE_ADDRESS_CONSTRAINTS: Record<
  DeviceType,
  { fixed_address?: number; auto_keys?: boolean }
> = {
  corridor_keypad: { auto_keys: true },
  standard_keypad: { auto_keys: false },
  keycard: { auto_keys: true, fixed_address: 1 },
  logic_board: { auto_keys: false }
};

export interface AutoCreatedKey {
  key_index: number;
  name: string;
  group_id: number;
  address: number;
}

export const AUTO_CREATED_KEYS: Record<DeviceType, AutoCreatedKey[]> = {
  corridor_keypad: [
    { key_index: 1, name: 'MMR', group_id: 2, address: 2 },
    { key_index: 2, name: 'Occupied', group_id: 2, address: 2 },
    { key_index: 3, name: 'Privacy', group_id: 2, address: 2 },
    { key_index: 4, name: 'Bell', group_id: 1, address: 1 }
  ],
  keycard: [
    { key_index: 1, name: 'Insert', group_id: 4, address: 1 },
    { key_index: 2, name: 'Remove', group_id: 4, address: 1 }
  ],
  standard_keypad: [],
  logic_board: []
};

export const CORRIDOR_BELL_GROUP_ID = 1;
export const CORRIDOR_BELL_ADDRESS = 1;

export const KEYCARD_GROUP_ID = 4;
export const KEYCARD_ADDRESS = 1;

export const CORRIDOR_KEYPAD_GROUP_ID = 2;
export const CORRIDOR_KEYPAD_ADDRESS = 2;

export const STANDARD_KEYPAD_GROUP_ID = 3;

/** Addresses always blocked in a group (corridor bell, corridor MMR keys, keycard). */
export const RESERVED_ADDRESSES_BY_GROUP: Record<number, number[]> = {
  1: [1],
  2: [2],
  4: [1]
};

const ADDRESSABLE_TYPES: DeviceType[] = ['standard_keypad', 'logic_board'];

export function getUsedAddressesByGroup(
  devices: InputDeviceResponse[]
): Record<number, Set<number>> {
  const used: Record<number, Set<number>> = {};
  for (const device of devices) {
    for (const key of device.keys) {
      if (!used[key.group_id]) used[key.group_id] = new Set();
      used[key.group_id].add(key.address);
    }
  }
  return used;
}

export function getAvailableAddresses(
  devices: InputDeviceResponse[],
  deviceType: DeviceType
): number[] {
  const allAddresses = Array.from({ length: 64 }, (_, i) => i + 1);
  const usedByGroup = getUsedAddressesByGroup(devices);
  const groupId = DEVICE_TYPE_TO_GROUP_ID[deviceType];
  const usedInGroup = usedByGroup[groupId] ?? new Set();
  const reserved = new Set(RESERVED_ADDRESSES_BY_GROUP[groupId] ?? []);
  return allAddresses.filter((a) => !usedInGroup.has(a) && !reserved.has(a));
}

export function getNextAvailableAddress(
  devices: InputDeviceResponse[],
  deviceType: DeviceType
): number | null {
  if (!ADDRESSABLE_TYPES.includes(deviceType)) return null;
  const available = getAvailableAddresses(devices, deviceType);
  return available.length > 0 ? available[0] : null;
}
